/**
 * The attribute allowlist and denylist behind `computeSelectorPath`
 * (Issue #13575).
 *
 * Split out from `selector-path.ts` because these are policy — the set of
 * attributes an author writes on purpose versus the set a component library
 * writes and rewrites as the user interacts — and policy is what changes when
 * the app adopts a new primitive. The algorithm does not.
 *
 * Data only: no imports, no DOM access, nothing to run. The pair can be
 * concatenated and injected as source text (Lightspeed discipline) precisely
 * because neither half reaches outside itself.
 */

/**
 * Rung 2. A test hook is the strongest signal the app can give: it exists for
 * no purpose other than naming one element, and it survives restyling.
 */
export const TEST_HOOK_ATTRIBUTES: readonly string[] = [
  'data-testid',
  'data-cy',
]

/**
 * Rung 3, in preference order. `data-surface` is this app's own anchor, so it
 * outranks the platform attributes; `name` / `aria-label` / `alt` are authored
 * for humans or for forms and therefore change only when the copy does.
 */
export const AUTHORED_ATTRIBUTES: readonly string[] = [
  'data-surface',
  'name',
  'aria-label',
  'alt',
]

/**
 * Rung 3's denylist, and the reason the rung can accept an unknown `data-*`
 * attribute at all.
 *
 * These are written by Radix (and the other headless libraries this app
 * pulls in through `@/components/ui/*`) to mirror INTERACTION state, so their
 * values flip on hover, focus, open and select. A path anchored on
 * `[data-state="open"]` resolves for exactly as long as the menu stays open —
 * which, for a feedback capture taken after the menu closes, is never.
 *
 * The second group is this app's OWN state vocabulary, which is not Radix's
 * and was missed by a denylist ported from one. Re-derive it rather than
 * guessing when the app adds more:
 *
 * ```
 * grep -rhoE "\sdata-[a-z0-9-]+=[\"{]" src/components src/app \
 *   | tr -d ' ={"' | sort | uniq -c | sort -rn
 * ```
 *
 * The test for inclusion is "does the user's own interaction flip it", not
 * "is it dynamic": `data-school-name` changes per row and is a fine anchor,
 * while `data-fold` changes on the next click and is not.
 *
 * The third group encodes POSITION. `[data-index="3"]` is exactly as
 * reorder-fragile as `:nth-of-type(4)`, which rung 5 correctly flags
 * `fragile: true` — so accepting it at rung 3, where `fragile` is false,
 * would tell the caller a position selector is stable. This app resolves by
 * `[data-index="${selectedIndex}"]` in four components, which is the proof
 * that the value IS the position.
 */
export const DENYLISTED_ATTRIBUTES: readonly string[] = [
  // Radix and other headless-library interaction state.
  'data-state',
  'data-orientation',
  'data-side',
  'data-align',
  'data-disabled',
  'data-highlighted',
  'data-selected',
  'data-placeholder',
  'data-swipe-direction',
  'data-swipe',
  'data-motion',
  'data-collapsed',
  'data-dragging',
  'data-resize-handle-state',
  // This app's own interaction and view state.
  'data-view-mode',
  'data-fold',
  'data-join-state',
  'data-home-state',
  'data-drop-target',
  'data-hidden',
  'data-locked',
  'data-reveal-frozen',
  'data-compact',
  'data-today',
  'data-animated',
  // Position, encoded as an attribute.
  'data-index',
  'data-list-index',
]

/** Whole families of library-owned attributes, denylisted by prefix. */
export const DENYLISTED_ATTRIBUTE_PREFIXES: readonly string[] = [
  'data-radix',
  'data-melt',
  'data-headlessui',
  'data-floating',
  'data-sonner',
  'data-react',
]

/**
 * The text rung's ceiling, in characters. Long text is a paragraph rather
 * than a label, and a paragraph is both a poor UI label and likely to be
 * edited. Also the truncation point for the returned `label`.
 */
export const MAX_TEXT_LENGTH = 60

/**
 * The attribute rung's ceiling. An attribute value longer than this is prose
 * (a description, a serialised payload) rather than a name.
 */
export const MAX_ATTRIBUTE_VALUE_LENGTH = 100

/** The separator in a `tag · text` label. */
export const LABEL_SEPARATOR = ' · '

/**
 * The path emitted when no rung produced one that resolves back to the
 * element — see `structuralPath` in the algorithm half.
 *
 * It is a syntactically valid attribute selector for an attribute nothing in
 * the app writes, so `document.querySelector` parses it and returns null.
 * That is deliberate: the caller's contract is that `resolveSelectorPath`
 * returns null when the element is gone, and an honest null is the right
 * answer for an element that was never addressable from the resolving
 * document. A path that merely FAILED to parse would rely on a thrown
 * exception for the same outcome, which is a weaker guarantee.
 */
export const UNRESOLVABLE_PATH = '[data-cadence-unresolvable-selector-path]'
